import {
  ColumnType,
  CellMatrix,
  ApiTablaDefResponse,
  EditableGridTransformed,
  GridColumnFromApi,
  GridRowDefFromApi,
  DataTypeConfig,
} from '../types/TablaDefTypes';

// ======================================================================
// Funciones auxiliares
// ======================================================================

/**
 * Mapea el código de tipo de dato de la API al tipo de columna del componente.
 */
function mapTipoDatoToColumnType(tipo: string): ColumnType {
  return ({ F: 'date', N: 'number', T: 'text' } as Record<string, ColumnType>)[tipo] || 'text';
}

/**
 * Normaliza fechas de la forma "ddMMyyyy" a "yyyy-MM-dd" (para <input type="date">).
 * Si viene "0" o vacío o undefined, devuelve "".
 * Si el formato no es el esperado, devuelve el valor original.
 */
function normalizeDate(value: string | undefined): string {
  if (!value || value === '0') return '';

  // Esperamos ddMMyyyy
  if (!/^\d{8}$/.test(value)) {
    return value;
  }

  const day = value.substring(0, 2);
  const month = value.substring(2, 4);
  const year = value.substring(4, 8);

  return `${year}-${month}-${day}`;
}

/**
 * Convierte y formatea un número según la cantidad de enteros y decimales,
 * utilizando formato localizado.
 *
 * @param value - valor a convertir (string o number)
 * @param enteros - cantidad máxima de dígitos enteros permitidos
 * @param decimales - cantidad exacta de decimales requeridos
 * @param locale - configuración regional (ej: "es-AR", "es-ES", "en-US")
 *
 * @returns string con el número formateado o null si no puede convertirse
 */
export function formatLocalizedNumber(value: string | number, enteros?: number, decimales?: number): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  // Convertir a número
  const num = typeof value === 'number' ? value : Number(value);

  if (isNaN(num)) {
    return '';
  }

  // Formatear localmente
  const formatter = new Intl.NumberFormat('en-US', {
    ...(decimales !== undefined ? { minimumFractionDigits: decimales, maximumFractionDigits: decimales } : {}),
    useGrouping: false,
  });

  return formatter.format(num);
}

/**
 * Devuelve el valor por de una celda según el tipo de columna.
 * (Para el componente, dejamos todo como string vacío, incluyendo números,
 * así no introducimos "0" donde no había dato).
 */
function getValueForType(type: ColumnType, value?: string, configParser?: DataTypeConfig): string {
  // Normalización de vacíos / undefined
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const { tablas_def_col_decimales: numDecimales, tablas_def_col_largo: numEntero } = configParser ?? {};

  if (type === 'date') {
    return normalizeDate(value);
  }

  if (type === 'number') {
    return formatLocalizedNumber(value, numEntero, numDecimales);
  }

  if (type === 'text') {
    return value;
  }

  return '';
}

// ======================================================================
// Función utilitaria de transformación
// ======================================================================

/**
 * Transforma la respuesta de la API (fil, col, dEmple)
 * en los parámetros esperados por el componente:
 * - filas (rows)
 * - columnas (columns)
 * - matriz de celdas (cells)
 *
 * Casos contemplados:
 * - Puede existir fila+columna sin dato en dEmple → la celda se crea con valor por defecto ('').
 * - Puede existir dato en dEmple con wdato vacío o undefined → se normaliza a '' (o formato fecha si corresponde).
 */
export function transformApiTablaDefToEditableGrid(api: ApiTablaDefResponse): EditableGridTransformed {
  // 1) Columnas: ordenadas por tablas_def_col_orden
  const columns: GridColumnFromApi[] = (api.col ?? [])
    .sort((a, b) => a.tablas_def_col_orden - b.tablas_def_col_orden)
    .map((c) => ({
      // campos mínimos que usa el componente
      id: c.tablas_def_col_id.toString(),
      label: c.tablas_def_col_titulo,
      type: mapTipoDatoToColumnType(c.tablas_def_col_tipo_dato),
      // metadatos completos de la API
      ...c,
    }));

  // 2) Filas: ordenadas por tablas_def_fil_orden
  const rows: GridRowDefFromApi[] = (api.fil ?? [])
    .sort((a, b) => a.tablas_def_fil_orden - b.tablas_def_fil_orden)
    .map((f) => ({
      // campos mínimos que usa el componente
      id: f.tablas_def_fil_id.toString(),
      label: f.tablas_def_fil_titulo,
      // metadatos completos de la API
      ...f,
    }));

  // 3) Construir matriz de celdas: cells[rowId][colId] = valor
  const cells: CellMatrix = {};

  // Mapa auxiliar de columnas por id numérico para conocer el tipo
  const colById = new Map<number, GridColumnFromApi>();
  for (const col of columns) {
    colById.set(col.tablas_def_col_id, col);
  }

  // 3.a) Cargamos los valores que vienen en dEmple
  for (const dato of api.dEmple) {
    const rowId = dato.fil_id.toString();
    const colId = dato.col_id.toString();

    cells[rowId] ??= {};

    const col = colById.get(dato.col_id);

    if (col === undefined) continue;

    const value = getValueForType(col.type, dato.wdato, col);

    cells[rowId][colId] = value;
  }

  // 3.b) Aseguramos que TODAS las combinaciones fila+columna tengan celda,
  // aunque no haya aparecido en dEmple → valor por defecto ('').
  for (const row of rows) {
    const rowCells = cells[row.id] || (cells[row.id] = {});
    for (const col of columns) {
      const colId = col.id;
      if (rowCells[colId] === undefined) {
        rowCells[colId] = getValueForType(col.type);
      }
    }
  }

  return {
    rows,
    columns,
    cells,
  };
}
